// src/utils/templateOrphan.js
//
// "Orphaning" a manuscript's text when its template changes.
//
// The ticket: switching the template on a manuscript that already has text
// leaves that text as regular text — the new template applies only to what is
// written next, and the author formats the old text by hand if they want it
// changed. A template's formatting is a theme, and a theme is a stylesheet over
// the whole book, so "only what is written next" has to be made true block by
// block: each existing block gets its CURRENT look written into its own inline
// styles, which outrank the stylesheet, and a `data-rv-orphan` attribute naming
// exactly the properties that were added. New blocks carry neither and follow
// the template.
//
// Undoing the switch releases those blocks again: the listed properties come
// off and the attribute with them. Properties the author set themselves are
// never touched — a block only lists what was added to it — and a block that
// was already orphaned by an earlier switch is left to that switch's undo.
//
// What inline styles cannot reach: things a theme draws with pseudo-elements —
// a drop cap or small-caps first line on a chapter's first paragraph, the
// ornament under a chapter title, a scene break's glyph. Page-level settings
// (margins, page size, where chapters start) belong to the whole book and
// follow the current template everywhere.
//
// Both functions are DOM-only and take how to read a block's look as a
// parameter, so the editor can read it with the theme switched off.

export const ORPHAN_ATTR = 'data-rv-orphan';

// The look a theme gives a block that inline styles can hold.
export const ORPHAN_PROPS = [
  'font-family', 'font-size', 'line-height', 'font-weight', 'font-style',
  'text-align', 'text-indent', 'letter-spacing', 'text-transform',
  'margin-top', 'margin-bottom', 'color',
];

// Blocks that are text. Images, tables, text boxes and scene breaks are drawn
// whole by the editor or the theme and have no font of their own to keep.
const SKIP = '.rv-end-spacer, .rv-resizable-image, .rv-table-wrap, .rv-textbox, .rv-scene-break';

function blocksOf(root) {
  const bodies = root.querySelectorAll('.rv-page-body');
  const list = bodies.length ? Array.from(bodies) : [root];
  return list.flatMap((body) => Array.from(body.children)).filter((el) => !el.matches(SKIP));
}

/**
 * Freeze every not-yet-orphaned text block at its current look.
 *
 * @param {Element} root the editor (or any element holding `.rv-page` sheets)
 * @param {(el: Element) => Object<string,string>} readStyle the block's look,
 *   one value per ORPHAN_PROPS entry (the editor passes computed styles read
 *   with the manuscript's theme switched off, so "regular" means unthemed)
 * @returns {string[]} the block ids frozen, for undo; a split paragraph's
 *   pieces share an id and it is listed once
 */
export function orphanBlocks(root, readStyle) {
  const ids = new Set();
  if (!root) return [];
  const blocks = blocksOf(root).filter((el) => !el.hasAttribute(ORPHAN_ATTR));
  // Read every block before writing any: a write can change how a later block
  // measures (margins collapse, lines rewrap), and the point is today's look.
  const looks = blocks.map((el) => readStyle(el) || {});
  blocks.forEach((el, i) => {
    const added = [];
    ORPHAN_PROPS.forEach((prop) => {
      const value = looks[i][prop];
      if (!value || el.style.getPropertyValue(prop)) return;
      el.style.setProperty(prop, value);
      added.push(prop);
    });
    el.setAttribute(ORPHAN_ATTR, added.join(','));
    const id = el.getAttribute('data-block-id');
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

/**
 * Release the blocks an undone switch froze: take off exactly the properties
 * that switch added. Returns how many blocks were released.
 */
export function unorphanBlocks(root, ids) {
  if (!root) return 0;
  const wanted = new Set(ids || []);
  let released = 0;
  blocksOf(root).forEach((el) => {
    if (!el.hasAttribute(ORPHAN_ATTR) || !wanted.has(el.getAttribute('data-block-id'))) return;
    releaseBlock(el);
    released += 1;
  });
  return released;
}

/**
 * Take one block out of the frozen state: the properties its switch added, and
 * the attribute. Also what a NEW block needs — pressing Enter in a frozen
 * paragraph clones its attributes and inline styles onto the new one, and new
 * writing is meant to follow the template. The editor calls this from
 * `ensureBlockIds` when it gives such a clone its own id.
 */
export function releaseBlock(el) {
  if (!el?.hasAttribute?.(ORPHAN_ATTR)) return false;
  (el.getAttribute(ORPHAN_ATTR) || '').split(',').filter(Boolean)
    .forEach((prop) => el.style.removeProperty(prop));
  el.removeAttribute(ORPHAN_ATTR);
  if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
  return true;
}

/** The computed values of ORPHAN_PROPS, as `orphanBlocks` wants them. */
export function computedLook(el) {
  const cs = el.ownerDocument.defaultView.getComputedStyle(el);
  return Object.fromEntries(ORPHAN_PROPS.map((p) => [p, cs.getPropertyValue(p)]));
}

/** The ids `orphanBlocks` would freeze, without changing anything. */
export function orphanableIds(root) {
  if (!root) return [];
  const ids = new Set();
  blocksOf(root).forEach((el) => {
    const id = el.getAttribute('data-block-id');
    if (id && !el.hasAttribute(ORPHAN_ATTR)) ids.add(id);
  });
  return Array.from(ids);
}
